import { useState, useContext } from 'react';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslation } from 'i18n';

import { TYPE_TO_READABLE_STRING_MAP } from 'constants/menuManagement';
import { fetchCubohItemChildren } from 'helpers/menuManagement';
import { logException } from 'helpers/loggers';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import { useMenuManagement } from 'hooks/useMenuManagement';
import {
  canEditMasterMenuItems,
  getMasterMenuDisabledTooltip,
  getCloneMenuDisabledTooltip,
  getPosSyncDisabledTooltip,
} from 'helpers/masterMenuPermissions';

import { ActionIcon, Menu, Tooltip, Text } from '@mantine/core';
import type { MenuItemProps } from '@mantine/core';
import DeleteMenuItemButton from 'components/MenuManagement/MenuItemActions/DeleteMenuItemButton';
import DuplicateMenuItemButton from 'components/MenuManagement/MenuItemActions/DuplicateItemButton';
import { EditDrawer } from 'components/MenuManagement/MenuItemActions/EditDrawer/EditDrawer';
import { isPosSyncEditableType } from 'components/MenuManagement/MenuItemActions/EditDrawer/helpers/posSyncEditableFields';
import MenuDragSortDrawer from 'components/MenuManagement/MenuEdit/MenuDragSort/MenuDragSortDrawer';
import { AddNewDrawers } from 'components/MenuManagement/MenuItemActions/AddNewDrawer/AddNewDrawers';
import { useAddNewDrawers } from 'components/MenuManagement/MenuItemActions/AddNewDrawer/hooks/useAddNewDrawers';
import { useHandleErrorNotification } from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';
import { EditIconSm } from '@chownow/pantry/icons';
import type { EditDrawerRecord } from 'components/MenuManagement/MenuItemActions/EditDrawer/types';
import type { MenuElement } from 'components/MenuManagement/MenuEdit/MenuDragSort/types';
import type { ParentRecord } from 'components/MenuManagement/MenuItemActions/types/newDrawerTypes';

const NS = 'menuManagementActions';

type GuardedMenuItemProps = MenuItemProps & {
  canPerform: boolean;
  tooltipLabel?: string;
  children: ReactNode;
  onClick?: () => void;
};

/* Wraps a disabled Menu.Item in a span so the reason tooltip still has a hover target */
const GuardedMenuItem: FunctionComponent<GuardedMenuItemProps> = ({
  canPerform,
  tooltipLabel,
  children,
  ...menuItemProps
}) => {
  const menuItem = (
    <Menu.Item {...menuItemProps} disabled={!canPerform}>
      {children}
    </Menu.Item>
  );

  if (!canPerform) {
    return (
      <Tooltip label={tooltipLabel} position="left" w="auto" maw="200px">
        <span>{menuItem}</span>
      </Tooltip>
    );
  }

  return menuItem;
};

type RenderActionsProps = {
  // Tree rows carry only part of a full record, so every field is read defensively
  record: Partial<ParentRecord>;
  group: keyof typeof TYPE_TO_READABLE_STRING_MAP;
  showAdd?: boolean;
  showEdit?: boolean;
};

export const RenderActions: FunctionComponent<RenderActionsProps> = ({
  record,
  group,
  showAdd = false,
  showEdit = false,
}) => {
  const { t } = useTranslation(NS);
  const { menu, refetchPhotos } = useContext(MenuEdit.Context);
  const { allCubohItemsState, getMerchantIntegrationsFromState } = useMenuManagement();
  const [editItemDrawerOpen, editItemDrawerHandlers] = useDisclosure(false);
  const [menuDragSortDrawerOpen, menuDragSortDrawerHandlers] = useDisclosure(false);
  const [targetParentSelectDrawerOpen, targetParentSelectDrawerHandlers] = useDisclosure(false);
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false);
  const [currentRecord, setCurrentRecord] = useState(record);
  const [sortableElements, setSortableElements] = useState<MenuElement[]>([]);
  const [sortDrawerError, setSortDrawerError] = useState('');
  const handleErrorNotification = useHandleErrorNotification();
  const itemReadableType = (TYPE_TO_READABLE_STRING_MAP[group] || group).toLowerCase();

  const hasLimitedAccess =
    menu?.masterMenuPermissionTier && !canEditMasterMenuItems(menu.masterMenuPermissionTier);
  const isPosSyncInPlace = !!getMerchantIntegrationsFromState(record?.merchantId || '')
    ?.isPosSyncInPlace;
  const canEdit = !record?.noEdit && !hasLimitedAccess && !isPosSyncInPlace;
  // Under POS sync the drawer still opens for types with merchant-owned fields (CN-50652).
  const canOpenEditDrawer =
    canEdit ||
    (isPosSyncInPlace && !record?.noEdit && !hasLimitedAccess && isPosSyncEditableType(group));
  const canSort = !record?.noEdit && !hasLimitedAccess;
  // Hidden, not disabled: the next sync overwrites any manual order (CN-51667).
  const showSort = !isPosSyncInPlace;

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  const {
    handleAddClick,
    isDisabled: isAddDisabled,
    disabledReason: addDisabledReason,
    newItemReadableType,
    drawerProps,
  } = useAddNewDrawers({ parentRecord: record });

  const fetchAndSetElements = async (forSorting = false) => {
    const response = await fetchCubohItemChildren({
      companyID: record.companyId,
      merchantID: record.merchantId,
      parentUuid: forSorting ? record.parentUuid : record.uuid,
    });
    const items: (MenuElement & { ordinal: number })[] = response.items || [];
    setSortableElements(items.slice().sort((a, b) => a.ordinal - b.ordinal));
    return items;
  };

  const handleEditClick = async () => {
    try {
      const items = await fetchAndSetElements();
      const itemKey = record.key ?? `${record.uuid}_${record.parentUuid}`;
      const freshRecord = allCubohItemsState[group]?.data?.[itemKey] ?? record;
      setCurrentRecord({
        ...freshRecord,
        // `record` comes from CategoryTree's latest fetchCubohItemChildren call (on accordion
        // open), which is more current than Redux for imageUrl — Redux items only update on
        // edit/create, not when the Photos tab changes a photo.
        imageUrl: record.imageUrl,
        children: items,
      });
      editItemDrawerHandlers.open();
    } catch (error) {
      // The drawer is the only way to edit a POS-synced row (CN-50652), so a failed
      // load has to surface - otherwise nothing opens and the user has no idea why.
      handleErrorNotification({ customMessage: t('RenderActions.editDrawerLoadError') });
      // logException replaces the error with its own message, so pass the reason via context.
      logException({
        message: 'Failed to load menu item children for the edit drawer',
        error,
        context: {
          reason: error instanceof Error ? error.message : String(error),
          itemType: group,
          uuid: record?.uuid,
        },
        tags: null,
      });
    }
  };

  const handleOpenSortDrawer = async () => {
    try {
      await fetchAndSetElements(true);
      setSortDrawerError('');
      menuDragSortDrawerHandlers.open();
    } catch (error) {
      // TODO: use GENERIC_QUERY_ERROR_MESSAGE from 'constants/error'
      setSortDrawerError(t('RenderActions.sortDrawerLoadError'));
      menuDragSortDrawerHandlers.open();
    }
  };

  let tooltipLabel = getCloneMenuDisabledTooltip(itemReadableType);
  if (hasLimitedAccess) {
    tooltipLabel = getMasterMenuDisabledTooltip();
  } else if (isPosSyncInPlace) {
    tooltipLabel = getPosSyncDisabledTooltip();
  }

  const sortTooltipLabel = hasLimitedAccess
    ? getMasterMenuDisabledTooltip()
    : getCloneMenuDisabledTooltip(itemReadableType);

  return (
    <>
      <Menu position={isMobile ? 'bottom-end' : 'right'}>
        <Menu.Target>
          <ActionIcon variant="ghost">
            <EditIconSm />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown w={240}>
          {showEdit && (
            <GuardedMenuItem
              canPerform={canOpenEditDrawer}
              tooltipLabel={tooltipLabel}
              onClick={() => void handleEditClick()}
            >
              <Text variant="label-md">
                {t('RenderActions.editMenuItem', { itemReadableType })}
              </Text>
            </GuardedMenuItem>
          )}
          <GuardedMenuItem
            canPerform={canEdit}
            tooltipLabel={tooltipLabel}
            onClick={targetParentSelectDrawerHandlers.open}
          >
            <Text variant="label-md">{t('RenderActions.copyMenuItem', { itemReadableType })}</Text>
          </GuardedMenuItem>
          {showSort && (
            <GuardedMenuItem
              canPerform={canSort}
              tooltipLabel={sortTooltipLabel}
              onClick={() => void handleOpenSortDrawer()}
            >
              <Text variant="label-md">
                {t('RenderActions.sortMenuItem', { itemReadableType })}
              </Text>
            </GuardedMenuItem>
          )}
          <GuardedMenuItem
            canPerform={canEdit}
            tooltipLabel={tooltipLabel}
            onClick={deleteModalHandlers.open}
          >
            <Text variant="label-md">
              {t('RenderActions.deleteMenuItem', { itemReadableType })}
            </Text>
          </GuardedMenuItem>
          {showAdd && (
            <>
              <Menu.Divider pt={0} mb="xs3" />
              <GuardedMenuItem
                canPerform={!isAddDisabled}
                tooltipLabel={addDisabledReason}
                onClick={handleAddClick}
              >
                <Text variant="label-md">
                  {t('RenderActions.addMenuItem', { newItemReadableType })}
                </Text>
              </GuardedMenuItem>
            </>
          )}
        </Menu.Dropdown>
      </Menu>
      {/* Outside <Menu>: the dropdown unmounts on close, taking a just-opened drawer with it */}
      {showAdd && <AddNewDrawers {...drawerProps} />}
      {showEdit && (
        <EditDrawer
          record={currentRecord as EditDrawerRecord}
          editItemDrawerOpen={editItemDrawerOpen}
          editItemDrawerHandlers={editItemDrawerHandlers}
          onSuccess={refetchPhotos}
        />
      )}
      <DuplicateMenuItemButton
        record={record}
        targetParentSelectDrawerOpen={targetParentSelectDrawerOpen}
        targetParentSelectDrawerHandlers={targetParentSelectDrawerHandlers}
      />
      <MenuDragSortDrawer
        opened={menuDragSortDrawerOpen}
        onClose={menuDragSortDrawerHandlers.close}
        type={group}
        sortableElements={sortableElements}
        fetchError={sortDrawerError}
      />
      <DeleteMenuItemButton
        record={record}
        group={group}
        menuId={menu?.uuid}
        deleteModalOpened={deleteModalOpened}
        deleteModalHandlers={deleteModalHandlers}
      />
    </>
  );
};
